// pages/public/Compra.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Users, DollarSign, Smartphone, FileText, UserCheck, IdCard, Phone, Building2, Banknote, CreditCard, Copy, Eye, Download, Home } from 'lucide-react';
import PublicLayout from '../../layouts/PublicLayout';
import { serviciosAPI } from '../../services/api';
import { pdf } from '@react-pdf/renderer';
import ComprobantePDF from '../../components/ComprobantePDF';
import Modal from '../../components/Modal';
import TotalConDivisas from '../../components/TotalConDivisas';
import { useDivisas } from '../../contexts/DivisasContext';

const API_URL = process.env.REACT_APP_API_URL;

const Compra = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { convertir } = useDivisas(); // ✅ NUEVO: para conversión del PDF
  const [loading, setLoading] = useState(false);
  const [metodosPago, setMetodosPago] = useState([]);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  
  const [servicio, setServicio] = useState(null);
  const [tramoIds, setTramoIds] = useState([]);
  const [cantidadPasajeros, setCantidadPasajeros] = useState(1);
  const [pasajerosAdicionales, setPasajerosAdicionales] = useState([]);
  const [metodoPago, setMetodoPago] = useState('');
  const [monedaMetodoPago, setMonedaMetodoPago] = useState(null);
  const [comprobante, setComprobante] = useState('');
  
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  const [titular, setTitular] = useState({
    nombre: '',
    cedula: '',
    telefonoWhatsapp: '',
    telefonoParticular: ''
  });

  useEffect(() => {
    if (location.state?.servicio) {
      const servicioData = location.state.servicio;
      setServicio(servicioData);
      setTramoIds(location.state?.tramoIds || []);
      cargarMetodosPago(servicioData.id);
      
      const cantidad = location.state.cantidadPasajeros || 1;
      setCantidadPasajeros(cantidad);
      if (cantidad > 1) {
        setPasajerosAdicionales(
          Array(cantidad - 1).fill(null).map(() => ({ nombre: '', cedula: '', esMenor: false }))
        );
      }
    } else {
      navigate('/servicios');
    }
  }, [location, navigate]);

  const cargarMetodosPago = async (servicioId) => {
    try {
      const response = await fetch(`${API_URL}/servicios/${servicioId}/metodos-pago`);
      if (!response.ok) throw new Error('Error al cargar métodos de pago');
      const data = await response.json();
      setMetodosPago(data);
      
      if (data.length > 0) {
        setMetodoPago(data[0].nombre);
        setMonedaMetodoPago(data[0].moneda || null);
        if (data[0].cuentas && data[0].cuentas.length > 0) {
          setCuentasBancarias(data[0].cuentas);
        } else {
          setCuentasBancarias([]);
        }
      }
    } catch (error) {
      console.error('Error al cargar métodos de pago:', error);
    }
  };

  const handleMetodoPagoChange = (metodo) => {
    setMetodoPago(metodo.nombre);
    setMonedaMetodoPago(metodo.moneda || null);
    if (metodo.cuentas && metodo.cuentas.length > 0) {
      setCuentasBancarias(metodo.cuentas);
    } else {
      setCuentasBancarias([]);
    }
    setComprobante('');
  };

  const handleCantidadChange = (e) => {
    const nuevaCantidad = parseInt(e.target.value);
    setCantidadPasajeros(nuevaCantidad);
    if (nuevaCantidad > 1) {
      setPasajerosAdicionales(
        Array(nuevaCantidad - 1).fill(null).map(() => ({ nombre: '', cedula: '', esMenor: false }))
      );
    } else {
      setPasajerosAdicionales([]);
    }
  };

  const handlePasajeroChange = (index, field, value) => {
    const nuevosPasajeros = [...pasajerosAdicionales];
    nuevosPasajeros[index] = { ...nuevosPasajeros[index], [field]: value };
    setPasajerosAdicionales(nuevosPasajeros);
  };

  const handleToggleMenor = (index) => {
    const nuevosPasajeros = [...pasajerosAdicionales];
    const esMenor = !nuevosPasajeros[index].esMenor;
    nuevosPasajeros[index] = {
      ...nuevosPasajeros[index],
      esMenor,
      cedula: esMenor ? '' : nuevosPasajeros[index].cedula
    };
    setPasajerosAdicionales(nuevosPasajeros);
  };

  const generarPDF = async (reserva) => {
    try {
      // ✅ Calcular conversión según la moneda del método de pago
      const conversion = convertir(reserva.total);
      const montoConvertido =
        monedaMetodoPago === 'VES'
          ? conversion.bolivares
          : monedaMetodoPago === 'BRL'
            ? conversion.reales
            : null;

      const blob = await pdf(
        <ComprobantePDF
          reserva={reserva}
          servicio={servicio}
          titular={titular}
          pasajerosAdicionales={pasajerosAdicionales}
          metodoPago={metodoPago}
          comprobante={comprobante}
          cantidadPasajeros={cantidadPasajeros}
          monedaMetodoPago={monedaMetodoPago}   // ✅ NUEVO
          montoConvertido={montoConvertido}      // ✅ NUEVO
        />
      ).toBlob();
      return blob;
    } catch (error) {
      console.error('❌ Error al generar PDF:', error);
      return null;
    }
  };

  const descargarPDF = async (reserva) => {
    try {
      const blob = await generarPDF(reserva);
      
      if (!blob) {
        setErrorMessage('No se pudo generar el comprobante');
        setShowErrorModal(true);
        return false;
      }
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `comprobante-${reserva.codigo}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      return true;
    } catch (error) {
      console.error('❌ Error en descarga:', error);
      setErrorMessage('No se pudo descargar el comprobante');
      setShowErrorModal(true);
      return false;
    }
  };

  const showConfirmPreview = () => {
    if (!titular.nombre || !titular.cedula || !titular.telefonoWhatsapp) {
      setErrorMessage('Completá todos los campos obligatorios del titular');
      setShowErrorModal(true);
      return;
    }

    if (cantidadPasajeros > 1) {
      const incompletos = pasajerosAdicionales.some(p => 
        !p.nombre || (!p.esMenor && !p.cedula)
      );
      if (incompletos) {
        setErrorMessage('Completá el nombre de todos los pasajeros. La cédula es obligatoria para mayores de edad.');
        setShowErrorModal(true);
        return;
      }
    }

    if (cuentasBancarias.length > 0 && !comprobante) {
      setErrorMessage('Ingresa el número de comprobante de la transferencia');
      setShowErrorModal(true);
      return;
    }

    if (!metodoPago) {
      setErrorMessage('Seleccioná un método de pago');
      setShowErrorModal(true);
      return;
    }

    setShowPreviewModal(true);
  };

  const ejecutarCompra = async () => {
    setShowPreviewModal(false);
    setLoading(true);
    
    try {
      const todosLosNombres = [
        titular.nombre,
        ...pasajerosAdicionales.map(p => p.nombre)
      ];
      
      const cedulasAdicionales = pasajerosAdicionales.map(p => 
        p.esMenor ? 'Menor de edad' : p.cedula
      );

      const data = {
        servicioId: servicio.id,
        cantidadPasajeros: cantidadPasajeros,
        nombresPasajeros: todosLosNombres,
        cedulaTitular: titular.cedula,
        cedulasPasajeros: cedulasAdicionales,
        nombrePasajero: titular.nombre,
        emailPasajero: 'cliente@email.com',
        telefonoPasajero: titular.telefonoWhatsapp,
        telefonoParticular: titular.telefonoParticular || '',
        metodoPago: metodoPago,
        comprobante: cuentasBancarias.length > 0 ? comprobante : null,
        tramoIds: tramoIds
      };

      const response = await serviciosAPI.crearReserva(data);
      
      let descargaExitosa = false;
      try {
        descargaExitosa = await descargarPDF(response);
      } catch (error) {
        console.error('❌ Error en descarga automática:', error);
      }

      setReservaData({
        ...response,
        descargaExitosa
      });
      
      setShowSuccessModal(true);

    } catch (error) {
      console.error('❌ Error al crear reserva:', error);
      setErrorMessage(error.message || 'No se pudo completar la reserva. Intentá nuevamente.');
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const formatPrecio = (precio) => {
    return new Intl.NumberFormat('es-AR').format(precio);
  };

  const getMetodoIcono = (nombre) => {
    switch(nombre) {
      case 'Efectivo': return <DollarSign className="w-4 h-4" />;
      case 'Transferencia': return <Smartphone className="w-4 h-4" />;
      case 'Pix': return <Smartphone className="w-4 h-4" />;
      case 'Tarjeta': return <CreditCard className="w-4 h-4" />;
      case 'Depósito': return <Banknote className="w-4 h-4" />;
      default: return <DollarSign className="w-4 h-4" />;
    }
  };

  const getLabelTotal = () => {
    if (monedaMetodoPago === 'VES') return 'Total a transferir (Bs)';
    if (monedaMetodoPago === 'BRL') return 'Total a transferir (R$)';
    return 'Total';
  };

  const [reservaData, setReservaData] = useState(null);

  if (!servicio) {
    return (
      <PublicLayout>
        <div className="container mx-auto px-4 py-16 text-center">
          <p className="text-gray-500">Cargando información del viaje...</p>
        </div>
      </PublicLayout>
    );
  }

  const total = servicio.precio * cantidadPasajeros;

  return (
    <PublicLayout>
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        <h1 className="text-2xl font-bold text-gray-800 mb-5">Confirmar compra</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <form onSubmit={(e) => { e.preventDefault(); showConfirmPreview(); }}>
                {/* Cantidad de pasajeros */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#e83e8c]" />
                    Cantidad de pasajeros
                  </label>
                  <select
                    value={cantidadPasajeros}
                    onChange={handleCantidadChange}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#e83e8c]/20 focus:border-[#e83e8c] focus:outline-none text-sm"
                  >
                    {[1, 2, 3, 4, 5, 6].map(num => (
                      <option key={num} value={num}>{num} {num === 1 ? 'pasajero' : 'pasajeros'}</option>
                    ))}
                  </select>
                </div>

                {/* Datos del titular */}
                <div className="mb-5 border border-gray-200 rounded-xl p-4 bg-white hover:border-[#e83e8c] transition-colors">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <UserCheck className="w-4 h-4 text-[#e83e8c]" />
                    Datos del titular (quien paga)
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2 md:col-span-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Nombre completo <span className="text-[#e83e8c]">*</span>
                      </label>
                      <input
                        type="text"
                        value={titular.nombre}
                        onChange={(e) => setTitular({...titular, nombre: e.target.value})}
                        placeholder="Ej: Juan Pérez"
                        className="w-full px-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#e83e8c]/20 focus:border-[#e83e8c] focus:outline-none text-sm"
                        required
                      />
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                        <IdCard className="w-3 h-3 text-[#e83e8c]" />
                        Cédula <span className="text-[#e83e8c]">*</span>
                      </label>
                      <input
                        type="text"
                        value={titular.cedula}
                        onChange={(e) => setTitular({...titular, cedula: e.target.value})}
                        placeholder="Ej: 12.345.678-9"
                        className="w-full px-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#e83e8c]/20 focus:border-[#e83e8c] focus:outline-none text-sm"
                        required
                      />
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                        <Phone className="w-3 h-3 text-[#e83e8c]" />
                        WhatsApp <span className="text-[#e83e8c]">*</span>
                      </label>
                      <input
                        type="tel"
                        value={titular.telefonoWhatsapp}
                        onChange={(e) => setTitular({...titular, telefonoWhatsapp: e.target.value})}
                        placeholder="Ej: 11 2345-6789"
                        className="w-full px-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#e83e8c]/20 focus:border-[#e83e8c] focus:outline-none text-sm"
                        required
                      />
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                        <Phone className="w-3 h-3 text-[#e83e8c]" />
                        Teléfono particular (opcional)
                      </label>
                      <input
                        type="tel"
                        value={titular.telefonoParticular}
                        onChange={(e) => setTitular({...titular, telefonoParticular: e.target.value})}
                        placeholder="Ej: 11 1234-5678"
                        className="w-full px-3 py-1.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#e83e8c]/20 focus:border-[#e83e8c] focus:outline-none text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Pasajeros adicionales */}
                {cantidadPasajeros > 1 && (
                  <div className="mb-5 border border-gray-200 rounded-xl p-4 bg-white hover:border-[#e83e8c] transition-colors">
                    <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#e83e8c]" />
                      Pasajeros adicionales ({cantidadPasajeros - 1})
                    </h3>
                    <div className="space-y-3">
                      {pasajerosAdicionales.map((pasajero, index) => (
                        <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-[#e83e8c]">Pasajero #{index + 2}</span>
                            <label className="flex items-center gap-1.5 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={pasajero.esMenor}
                                onChange={() => handleToggleMenor(index)}
                                className="w-3.5 h-3.5 text-[#e83e8c] focus:ring-[#e83e8c] rounded cursor-pointer"
                              />
                              <span className="text-xs font-medium text-gray-600 flex items-center gap-1">
                                Menor de edad
                              </span>
                            </label>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2 md:col-span-1">
                              <input
                                type="text"
                                placeholder="Nombre completo *"
                                value={pasajero.nombre}
                                onChange={(e) => handlePasajeroChange(index, 'nombre', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#e83e8c]/20 focus:border-[#e83e8c] focus:outline-none bg-white"
                                required
                              />
                            </div>
                            <div className="col-span-2 md:col-span-1">
                              <input
                                type="text"
                                placeholder={pasajero.esMenor ? "No requiere cédula" : "Cédula *"}
                                value={pasajero.cedula}
                                onChange={(e) => handlePasajeroChange(index, 'cedula', e.target.value)}
                                disabled={pasajero.esMenor}
                                className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#e83e8c]/20 focus:border-[#e83e8c] focus:outline-none ${
                                  pasajero.esMenor
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
                                    : 'bg-white border-gray-200'
                                }`}
                                required={!pasajero.esMenor}
                              />
                            </div>
                          </div>
                          {pasajero.esMenor && (
                            <p className="text-[11px] text-gray-500 mt-1.5 italic">
                              * Los menores de edad no requieren cédula.
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Método de pago */}
                <div className="mb-5 border border-gray-200 rounded-xl p-4 bg-white hover:border-[#e83e8c] transition-colors">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-[#e83e8c]" />
                    Método de pago
                  </h3>
                  
                  {metodosPago.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">
                      No hay métodos de pago disponibles para este servicio.
                      <br />
                      <span className="text-xs">Contactá con atención al cliente.</span>
                    </p>
                  ) : (
                    <>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        {metodosPago.map((metodo) => (
                          <button
                            key={metodo.id}
                            type="button"
                            onClick={() => handleMetodoPagoChange(metodo)}
                            className={`p-2.5 rounded-lg border-2 transition-all flex items-center justify-center gap-2 text-sm ${
                              metodoPago === metodo.nombre
                                ? 'border-[#e83e8c] bg-pink-50 text-[#e83e8c]'
                                : 'border-gray-200 hover:border-[#e83e8c] hover:bg-pink-50 text-gray-600'
                            }`}
                          >
                            {getMetodoIcono(metodo.nombre)}
                            {metodo.nombre}
                          </button>
                        ))}
                      </div>

                      {cuentasBancarias.length > 0 && (
                        <div className="mt-4 border border-gray-200 rounded-xl p-4 bg-white hover:border-[#e83e8c] transition-colors">
                          <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-[#e83e8c]" />
                            Datos para {metodoPago}
                          </h4>
                          
                          {cuentasBancarias.map((cuenta, index) => (
                            <div key={cuenta.id || index} className="space-y-2 text-sm">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Banco</label>
                                <div className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                                  <span className="text-gray-800">{cuenta.nombreBanco}</span>
                                </div>
                              </div>

                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Clave / ID</label>
                                <div className="flex items-center bg-gray-50 rounded-lg px-3 py-2 border border-gray-200 font-mono">
                                  <span className="text-gray-800">{cuenta.clave}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      navigator.clipboard.writeText(cuenta.clave);
                                      setErrorMessage('¡Copiado al portapapeles!');
                                      setShowErrorModal(true);
                                      setTimeout(() => setShowErrorModal(false), 1500);
                                    }}
                                    className="ml-2 p-1 text-gray-400 hover:text-[#e83e8c] transition-colors"
                                    title="Copiar"
                                  >
                                    <Copy className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>

                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Titular</label>
                                <div className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                                  <span className="text-gray-800">{cuenta.titular}</span>
                                </div>
                              </div>

                              {cuenta.email && (
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                                  <div className="bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                                    <span className="text-gray-800 text-sm">{cuenta.email}</span>
                                  </div>
                                </div>
                              )}

                              {index < cuentasBancarias.length - 1 && (
                                <hr className="my-3 border-gray-200" />
                              )}
                            </div>
                          ))}

                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                              <FileText className="w-4 h-4 text-[#e83e8c]" />
                              Número de operacion( COPIAR EL NUMERO COMPLETO ) <span className="text-[#e83e8c]">*</span>
                            </label>
                            <p className="text-xs text-gray-500 mb-2">
                              Ingresa el número de comprobante de tu {metodoPago} para que podamos verificar el pago.
                            </p>
                            <input
                              type="text"
                              value={comprobante}
                              onChange={(e) => setComprobante(e.target.value)}
                              placeholder="Ej: TRANS-12345 o número de referencia"
                              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e83e8c]/20 focus:border-[#e83e8c] focus:outline-none text-sm bg-white"
                              required={cuentasBancarias.length > 0}
                            />
                          </div>
                        </div>
                      )}

                      {metodosPago.length > 0 && cuentasBancarias.length === 0 && (
                        <div className="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-100">
                          <p className="text-sm text-yellow-800">
                            💵 El pago se realiza en <strong>{metodoPago}</strong> en la terminal de la empresa.
                            <br />
                            <span className="text-xs">Recuerda llegar con <strong>2 hora de anticipación</strong>.</span>
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || metodosPago.length === 0}
                  className="w-full bg-[#e83e8c] hover:bg-[#c2185b] text-white py-2.5 rounded-xl text-sm font-medium transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  {loading ? 'Procesando...' : 'Revisar y confirmar compra'}
                </button>
              </form>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sticky top-6">
              <h2 className="text-base font-semibold text-gray-800 mb-3">Resumen del viaje</h2>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Origen</span>
                  <span className="font-medium">{servicio.horario.origen}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Destino</span>
                  <span className="font-medium">{servicio.horario.destino}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Fecha</span>
                  <span className="font-medium">{new Date(servicio.fecha).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Hora</span>
                  <span className="font-medium">{servicio.horario.salida}</span>
                </div>

                <hr className="border-gray-200" />

                <div className="flex justify-between">
                  <span className="text-gray-500">Precio por persona</span>
                  <span className="font-medium">${formatPrecio(servicio.precio)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Pasajeros</span>
                  <span className="font-medium">× {cantidadPasajeros}</span>
                </div>

                <hr className="border-gray-200" />

                <TotalConDivisas 
                  montoUSD={servicio.precio * cantidadPasajeros} 
                  label={getLabelTotal()}
                  variante="default"
                  moneda={monedaMetodoPago}
                />

                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-400 text-center">
                    Método de pago: {metodoPago || 'Seleccioná uno'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL DE PREVIEW */}
      <Modal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        title="Confirmar compra"
        confirmText="Confirmar compra"
        cancelText="Cancelar"
        onConfirm={ejecutarCompra}
        closeOnBackdrop={false}
      >
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-1 bg-gray-50 p-3 rounded-lg">
            <span className="text-gray-500">Origen:</span>
            <span className="font-medium">{servicio.horario.origen}</span>
            <span className="text-gray-500">Destino:</span>
            <span className="font-medium">{servicio.horario.destino}</span>
            <span className="text-gray-500">Fecha:</span>
            <span className="font-medium">{new Date(servicio.fecha).toLocaleDateString()}</span>
            <span className="text-gray-500">Hora:</span>
            <span className="font-medium">{servicio.horario.salida}</span>
          </div>

          <div className="grid grid-cols-2 gap-1 bg-gray-50 p-3 rounded-lg">
            <span className="text-gray-500">Titular:</span>
            <span className="font-medium">{titular.nombre}</span>
            <span className="text-gray-500">Cédula:</span>
            <span className="font-medium">{titular.cedula}</span>
            <span className="text-gray-500">WhatsApp:</span>
            <span className="font-medium">{titular.telefonoWhatsapp}</span>
            {titular.telefonoParticular && (
              <>
                <span className="text-gray-500">Tel. particular:</span>
                <span className="font-medium">{titular.telefonoParticular}</span>
              </>
            )}
          </div>

          {pasajerosAdicionales.length > 0 ? (
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="font-semibold text-gray-700 text-xs mb-2">
                Pasajeros adicionales ({pasajerosAdicionales.length})
              </div>
              <div className="space-y-1 text-sm text-gray-800">
                {pasajerosAdicionales.map((pasajero, index) => (
                  <div key={index} className="flex items-center gap-2 flex-wrap">
                    <span>{index + 1}. {pasajero.nombre}</span>
                    <span className="text-gray-400">·</span>
                    {pasajero.esMenor ? (
                      <span className="text-xs text-purple-600 bg-purple-50 border border-purple-200 rounded px-1.5 py-0.5 font-medium flex items-center gap-1">
                        Menor de edad
                      </span>
                    ) : (
                      <span className="text-gray-500">Cédula: {pasajero.cedula}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="font-semibold text-gray-700 text-xs mb-1">Pasajeros adicionales</div>
              <div className="text-sm text-gray-400 italic">No hay pasajeros adicionales</div>
            </div>
          )}

          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex justify-between font-semibold text-gray-700 text-xs">
              <span>Método de pago</span>
              <span>{metodoPago}</span>
            </div>
            {cuentasBancarias.length > 0 && (
              <div className="text-xs text-gray-500 mt-1 border-t border-gray-200 pt-1 flex flex-wrap gap-2">
                <span>Banco: <strong>{cuentasBancarias[0].nombreBanco}</strong></span>
                <span>Clave: <strong>{cuentasBancarias[0].clave}</strong></span>
                <span>Comprobante: <strong>{comprobante}</strong></span>
              </div>
            )}
          </div>

          <TotalConDivisas 
            montoUSD={servicio.precio * cantidadPasajeros} 
            label={getLabelTotal()}
            variante="compact"
            moneda={monedaMetodoPago}
          />
        </div>
      </Modal>

      {/* MODAL DE ÉXITO */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          navigate('/');
        }}
        title="Reserva confirmada"
        confirmText=""
        cancelText=""
        showCancelButton={false}
        onConfirm={() => {}}
        closeOnBackdrop={false}
        footer={
          <div className="flex gap-3 w-full">
            <button
              onClick={() => {
                if (reservaData) {
                  descargarPDF(reservaData);
                }
              }}
              className="flex-1 px-3 py-1.5 bg-[#e83e8c] hover:bg-[#c2185b] text-white rounded-lg text-sm font-medium transition-all hover:shadow-lg flex items-center justify-center gap-1.5"
            >
              <Download className="w-3.5 h-3.5" />
              Descargar comprobante
            </button>
            <button
              onClick={() => {
                setShowSuccessModal(false);
                navigate('/');
              }}
              className="flex-1 px-3 py-1.5 border-2 border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium flex items-center justify-center gap-1.5"
            >
              <Home className="w-3.5 h-3.5" />
              Ir al inicio
            </button>
          </div>
        }
      >
        {reservaData && (
          <div className="space-y-3 text-sm">
            <div className="bg-gray-50 p-3 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-500">Código:</span>
                <span className="font-mono font-medium text-[#e83e8c]">{reservaData.codigo}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total:</span>
                <span className="font-bold text-[#e83e8c]">${reservaData.total.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Pasajeros:</span>
                <span className="font-medium">{reservaData.cantidadPasajeros}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Método de pago:</span>
                <span className="font-medium">{metodoPago}</span>
              </div>
              {cuentasBancarias.length > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Comprobante:</span>
                  <span className="font-medium">{comprobante}</span>
                </div>
              )}
            </div>

            <TotalConDivisas 
              montoUSD={reservaData.total} 
              label={getLabelTotal()}
              variante="compact"
              moneda={monedaMetodoPago}
            />

            {cuentasBancarias.length > 0 ? (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 text-blue-800 text-xs">
                <strong>{metodoPago}:</strong> Su comprobante está siendo verificado. Recibirá un mensaje de confirmación una vez que se valide el pago.
              </div>
            ) : (
              <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-100 text-yellow-800 text-xs">
                <strong>Importante:</strong> El pago debe realizarse en caja <strong>1 hora antes</strong> de la salida del viaje.
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* MODAL DE ERROR */}
      <Modal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Error"
        confirmText="Aceptar"
        cancelText=""
        showCancelButton={false}
        onConfirm={() => setShowErrorModal(false)}
        closeOnBackdrop={false}
      >
        <div className="text-center py-4">
          <p className="text-gray-700">{errorMessage}</p>
        </div>
      </Modal>
    </PublicLayout>
  );
};

export default Compra;